import { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { get, set } from 'local-storage'
import { uniq } from 'lodash'
import Image from 'next/image'

import {
  Option,
  OptGroup,
  Select,
  SelectProps,
} from '../shared-components/BUI/Select'
import { DozerLoading } from '../shared-components/DozerLoading'

import { useUser } from '@/context/userContext'
import { SA_GET_BUILDERS } from '@/modules/builder/graphQL'
import { filterSelect } from '@/utils/index'

import bpLogo from 'public/buildpass-small.svg'

import {
  SaBuildersUiQuery,
  SaBuildersUiQueryVariables,
} from 'types/api.graphql'

const LS_KEY = 'bp-recent-builders'

type Option = {
  children: string | string[] | object[] // the user friendly text of the option
  key: string // the id of the option
  value: string // the id of the option
}

type Props = SelectProps & {
  showRecentBuilders?: boolean
  autoOpenAndFocus?: boolean
}

export const SaBuilderSelect: React.FC<Props> = ({
  onChange,
  showRecentBuilders = true,
  autoOpenAndFocus = false,
  ...selectProps
}) => {
  const { builder } = useUser()

  const { data, loading } = useQuery<
    SaBuildersUiQuery,
    SaBuildersUiQueryVariables
  >(SA_GET_BUILDERS)

  const recentBuilders = useMemo(() => {
    if (!showRecentBuilders) return []

    const recentBuilders = get<string>(LS_KEY)
    if (recentBuilders) {
      try {
        const recentBuildersParsed = JSON.parse(recentBuilders)
        return uniq([builder?.id, ...recentBuildersParsed])
      } catch {
        return []
      }
    }

    return []
  }, [showRecentBuilders, builder?.id])

  const onBuilderSelect = (builderId: string, option) => {
    const newRecentBuilders = uniq([builderId, ...recentBuilders]).slice(0, 3)
    set(LS_KEY, JSON.stringify(newRecentBuilders))

    onChange(builderId, option)
  }

  if (loading) {
    return <DozerLoading size={32} loading relative text="Loading builders" />
  }

  return (
    <Select
      autoFocus={autoOpenAndFocus}
      defaultOpen={autoOpenAndFocus}
      {...selectProps}
      showSearch
      onChange={onBuilderSelect}
      optionFilterProp="children"
      filterOption={(input, option) =>
        filterSelect(input, option as unknown as Option)
      }
    >
      {!!recentBuilders?.length && (
        <OptGroup label="Recent builders">
          {recentBuilders
            .filter((builderId) => {
              return data?.saBuilders?.find((b) => b.id === builderId)
            })
            .map((builderId) => {
              const builder = data?.saBuilders?.find((b) => b.id === builderId)
              return getBuilderOption(builder, 'recent-')
            })}
        </OptGroup>
      )}

      <OptGroup label="All builders">
        {data?.saBuilders?.map((builder) => getBuilderOption(builder, 'all-'))}

        {!data?.saBuilders?.length && (
          <Option value="">No builders found</Option>
        )}
      </OptGroup>
    </Select>
  )
}

export const getBuilderOption = (
  builder: SaBuildersUiQuery['saBuilders'][number],
  prefix: string
) => {
  if (!builder) return null

  return (
    <Option key={prefix + builder.id} value={builder.id}>
      <Image
        src={builder.logoUrl ?? bpLogo.src}
        alt={builder.company.name}
        width={40}
        height={24}
        style={{
          marginRight: '0.5rem',
          objectFit: 'contain',
          objectPosition: 'center',
          ...(!builder?.logoUrl && {
            transform: 'scale(0.6)',
            filter: 'grayscale(1) brightness(0.5)',
          }),
        }}
      />
      {builder.company.name}
    </Option>
  )
}
